import { Env, ProviderConfig, InitAuthRequest, BaseResponse, AuthenticationData } from "./common";
import { GridEnvironment } from "../api/grid-api-client";

/** @hidden */
export interface PasskeyConfig extends ProviderConfig {
    // Clean interface - uses base ProviderConfig fields
  }

/** @hidden */
export interface CreatePasskeySessionRequest {
  sessionKey: SessionKey;
  env: Env | GridEnvironment;
  metaInfo: Record<string, any>;
}

/** @hidden */
export interface CreatePasskeySessionResponse extends BaseResponse<{
  url: string;
}> {}

/**
 * Session Key
 * @property {any} key - The key
 * @property {number} expiration - The expiration
 */
export interface SessionKey { key: any; expiration: number; }

// Backend expects key as byte array for submit endpoint
/** @hidden */
export interface SessionKeyBackend {
  key: number[]; // Pubkey as byte array
  expiration: number; // u64
}

/** @hidden */
export interface MetaInfo {
  appName: string;
  redirectUrl?: string;
}

/** @hidden */
export type CeremonyType = 'create' | 'auth';

/**
 * Passkey Request
 */
export interface PasskeyRequest {
  env: Env;
  appName?: string;
  userId?: string;
  redirectUrl?: string;
  challenge: string;
  slotNumber: string;
  sessionKey: string;
  expirationInSeconds: string;
}

/**
 * Authorize Passkey Session Request
 */
export interface AuthorizePasskeySessionRequest {
  sessionKey?: SessionKey;
  metaInfo: MetaInfo;
  baseUrl?: string;
}

/**
 * Authorize Passkey Session Response
 */
export interface AuthorizePasskeySessionResponse extends BaseResponse<{
  url: string;
}> {}

// Submit Passkey Session
/**
 * Submit Passkey Session Request
 */
export interface SubmitPasskeySessionRequest {
  ceremonyType: CeremonyType;
  sessionKey: SessionKeyBackend; // Backend expects byte array format
  slotNumber: number;
  authenticatorResponse: any; // JSON value from WebAuthn
}

/**
 * Submit Passkey Session Response
 * @property {string} passkeyAccount - The passkey signer account address
 * @property {string} address - The smart account address associated with this passkey
 * @property {SessionKey} sessionKey - Session key for transaction signing
 */
export interface SubmitPasskeySessionResponse extends BaseResponse<{
  passkeyAccount: string;
  smartAccount: {
    address: string;  
  };
  sessionKey: SessionKey;
}> {}

// Find Passkey Account
/**
 * Find Passkey Account Request
 */
export interface FindPasskeyAccountRequest {
  sessionKey: SessionKey;
  authenticatorResponse: any;
}

/**
 * Find Passkey Account Response
 * @property {string} passkeyAccount - The passkey signer account address
 * @property {string} address - The smart account address associated with this passkey
 * @property {SessionKey} sessionKey - Session key for transaction signing
 */
export interface FindPasskeyAccountResponse extends BaseResponse<{
  passkeyAccount: string;
  address: string;
  sessionKey: SessionKey;
}> {}

/**
 * Get Passkey Session Response
 */
export interface GetPasskeySessionResponse extends BaseResponse<{
  // TODO: Define based on backend response
  session?: any;
}> {}

/**
 * Get Passkey Account Response
 * @property {string} relyingPartyId - The relying party ID (domain) associated with this passkey
 * @property {string} pubkey - The public key in base58 format
 * @property {SessionKey} [sessionKey] - Optional session key if not expired
 */
export interface GetPasskeyAccountResponse extends BaseResponse<{
  relyingPartyId: string;
  pubkey: string;
  sessionKey?: SessionKey;
}> {}

/**
 * Create Passkey Account Request
 * Creates both the passkey account (externally signed account) and smart account in one call
 * @property {SessionKey} sessionKey - Session key for the passkey account
 * @property {number} slotNumber - Slot number for the passkey ceremony
 * @property {any} authenticatorResponse - WebAuthn authenticator response from passkey creation
 * @property {string} [adminAddress] - Optional admin address for settings authority
 * @property {string} [memo] - Optional memo for the account creation
 */
export interface CreatePasskeyAccountRequest {
  sessionKey: SessionKey;
  slotNumber: number;
  authenticatorResponse: any;
  adminAddress?: string;
  memo?: string;
}

/**
 * Passkey Account Policies for newly created accounts
 * @property {number} threshold - Signature threshold for the account
 * @property {string} [adminAddress] - Optional admin address
 */
export interface PasskeyAccountPolicies {
  threshold: number;
  adminAddress?: string;
}

/**
 * Passkey Account Info returned from account creation
 * @property {string} passkeyAccount - The passkey account address
 * @property {string} pubkey - The public key in base58 format
 * @property {string} relyingPartyId - The relying party ID (domain)
 * @property {SessionKey} sessionKey - Session key associated with the passkey account
 */
export interface PasskeyAccountInfo {
  passkeyAccount: string;
  pubkey: string;
  relyingPartyId: string;
  sessionKey: SessionKey;
}

/**
 * Create Passkey Account Response
 * @property {string} address - The created smart account address
 * @property {string} type - Account type (always 'passkey')
 * @property {string} status - Account status (e.g., 'active')
 * @property {PasskeyAccountPolicies} policies - Account policies including threshold and admin
 * @property {AuthenticationData} authentication - Array of session providers with authentication data
 * @property {string} createdAt - ISO timestamp when account was created
 * @property {string} updatedAt - ISO timestamp when account was last updated
 */
export interface CreatePasskeyAccountResponse extends BaseResponse<{
  address: string;
  type: string;
  status: string;
  policies: PasskeyAccountPolicies;
  authentication: AuthenticationData;
  createdAt: string;
  updatedAt: string;
}> {}


/** @hidden */
export const RP_NAME = 'Grid Auth'; // TODO: check if this is correct

/**
 * Passkey Credential Data
 */
export interface PasskeyCredentialData {
  id: ArrayBuffer;           // Raw ArrayBuffer for use in allowCredentials
  publicKey: string;         // Base64 encoded public key
  credentialId: string;      // Base64url encoded ID string
}

/**
 * Passkey Assertion Response
 */
export interface PasskeyAssertionResponse {
  response: {
    signature: string;
    clientDataJSON: string;
    authenticatorData: string;
    publicKey?: string;      // Optional, only present when credentialData is provided
  };
}
